import { AxiosError } from "axios";
import { ADMIN_API_ENDPOINTS } from "@/config/apiEndpoints/adminUrls";
import { handleApiError } from "@/lib/utils";
import adminAxios from "@/lib/axios";

export interface ISemester {
  id?: number;
  program_structure_id?: number;
  name: string;
  name_color?: string;
  section_color?: string;
  selected_section_color?: string;
  selected_section_bar_color?: string;
  header_text_1?: string;
  header_text_1_color?: string;
  header_text_2?: string;
  header_text_2_color?: string;
  priority: number;
  status?: boolean;
  subjects?: ISubject[];
}

export interface ISubject {
  id?: number;
  semester_id: number;
  subject_name: string;
  subject_name_text_color?: string;
  /** Optional — null/undefined when the subject has no credits. */
  credits?: number | null;
  credits_text_color?: string;
  priority: number;
  status?: boolean;
}

export interface IProgramStructure {
  id?: number;
  page_id?: number;
  component_id?: number;
  title?: string;
  title_text_color?: string;
  image?: string;
  alt_image?: string;
  container_background_color?: string;
  header_section_color?: string;
  subject_container_color?: string;
  cta_button_text?: string;
  cta_button_text_color?: string;
  cta_button_color?: string;
  cta_button_icon?: string;
  cta_button_icon_alt?: string;
  status?: boolean;
  semesters?: ISemester[];
}

// Semester API methods
export async function createSemester(payload: Partial<ISemester>) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE.SEMESTER.CREATE,
      payload
    );

    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateSemester(id: number, payload: Partial<ISemester>) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE.SEMESTER.UPDATE(id),
      payload
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function deleteSemester(id: number) {
  try {
    const response = await adminAxios.delete(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE.SEMESTER.DELETE(id)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getSemestersByComponentId(componentId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE.SEMESTER.GET_BY_COMPONENT_ID(componentId)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

// ProgramStructure API methods
export async function createProgramStructure(payload: Partial<IProgramStructure>) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE.CREATE,
      payload
    );

    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateProgramStructure(id: number, payload: Partial<IProgramStructure>) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE.UPDATE(id),
      payload
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getProgramStructureById(id: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE.GET_BY_ID(id)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getProgramStructureByComponentId(componentId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE.GET_BY_COMPONENT_ID(componentId)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function deleteProgramStructure(id: number) {
  try {
    const response = await adminAxios.delete(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE.DELETE(id)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

// Subject API methods (for subjects within semesters)
export async function createSubject(payload: Partial<ISubject>) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE.SUBJECT.CREATE,
      payload
    );

    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateSubject(id: number, payload: Partial<ISubject>) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE.SUBJECT.UPDATE(id),
      payload
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getSubjectsBySemesterId(semesterId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE.SUBJECT.GET_BY_SEMESTER_ID(semesterId)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function deleteSubject(id: number) {
  try {
    const response = await adminAxios.delete(
      ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE.SUBJECT.DELETE(id)
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function cloneProgramStructureWithSemesters(
  sourceProgramStructureId: number,
  targetPageComponentId: number,
  targetProgramStructureId?: number | null
) {
  try {
    const response = await adminAxios.post(ADMIN_API_ENDPOINTS.PROGRAM_STRUCTURE.CLONE,
      {
        source_program_structure_id: sourceProgramStructureId,
        target_page_component_id: targetPageComponentId,
        target_program_structure_id: targetProgramStructureId || null,
      }
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

